import { createRemainingTimeEstimator } from './create-remaining-time-estimator';
import { UploadProgressItem } from './models/upload-progress-item';
import { UploadProgress } from './models/upload-progress';
import { UploadProgressTracker } from './models/upload-progress-tracker';

// An item whose percent hasn't moved is still re-emitted this often, so the time left keeps up with
// the connection during the minutes one percent of a large upload can take.
const PROGRESS_REFRESH_MS = 1000;

type ItemState = {
  totalBytes: number;
  storedBytes: number;
  // Bytes sent by the parts still uploading — several upload at once.
  inFlightBytes: number;
  // Highest count already emitted — an item's bytes must never go down.
  reportedBytes: number;
  // What was last emitted: emitting on every byte report would flood the caller.
  emittedPercent: number;
  emittedAtMs: number;
  remainingTime: (syncedBytes: number, totalBytes: number) => number | undefined;
};

/**
 * Turns the raw byte reports of a chunked upload into progress worth showing: per item, the bytes
 * stored server-side plus those in flight, an estimated time left, and one emission per visible
 * change (a whole percent, or a second passing so the estimate doesn't go stale).
 *
 * Knows nothing of what is being uploaded or how — items are identified by the caller's own ids.
 */
export const createUploadProgressTracker = (
  items: UploadProgressItem[],
  onProgress: (progress: UploadProgress) => void,
): UploadProgressTracker => {
  const states = new Map<string, ItemState>(
    items.map(({ id, totalBytes }) => [
      id,
      {
        totalBytes,
        storedBytes: 0,
        inFlightBytes: 0,
        reportedBytes: 0,
        emittedPercent: -1,
        emittedAtMs: 0,
        remainingTime: createRemainingTimeEstimator(),
      },
    ]),
  );
  // Which item a part belongs to, and how much of it was counted so far: the caller tags each part,
  // its transport only reports that part's own running total, so its share is a delta from here.
  const trackedParts = new WeakMap<object, { itemId: string; countedBytes: number }>();

  const emit = (itemId: string, state: ItemState): void => {
    const { totalBytes } = state;
    const countedBytes = Math.min(state.storedBytes + state.inFlightBytes, totalBytes);
    // The count can dip by up to one part (a retried part drops its tally, and stored bytes may be
    // an estimate). A dip would read as a stall, so keep the highest.
    const syncedBytes = Math.max(countedBytes, state.reportedBytes);
    state.reportedBytes = syncedBytes;
    // Measured on every report, even when nothing is emitted: one percent can be minutes of upload.
    const remainingSeconds = state.remainingTime(syncedBytes, totalBytes);

    const percent = totalBytes === 0 ? 100 : Math.floor((syncedBytes / totalBytes) * 100);
    const hasMovedOnScreen = percent !== state.emittedPercent;
    const isEstimateStale = Date.now() - state.emittedAtMs >= PROGRESS_REFRESH_MS;
    if (!hasMovedOnScreen && !isEstimateStale) {
      return;
    }
    state.emittedPercent = percent;
    state.emittedAtMs = Date.now();
    onProgress({ id: itemId, syncedBytes, totalBytes, remainingSeconds });
  };

  const stateOfPart = (
    part: object,
  ): { tracked: { itemId: string; countedBytes: number }; state: ItemState } | undefined => {
    const tracked = trackedParts.get(part);
    const state = tracked && states.get(tracked.itemId);
    return tracked && state ? { tracked, state } : undefined;
  };

  return {
    trackPart: (part, itemId): void => {
      trackedParts.set(part, { itemId, countedBytes: 0 });
    },

    reportPartBytes: (part, sentBytes): void => {
      const found = stateOfPart(part);
      if (!found) {
        return;
      }
      const { tracked, state } = found;
      state.inFlightBytes += sentBytes - tracked.countedBytes;
      tracked.countedBytes = sentBytes;
      emit(tracked.itemId, state);
    },

    // On success the stored bytes now cover this part, and a failed part is retried as a fresh one —
    // either way its own tally has to go, or those bytes would be counted twice.
    partEnded: (part): void => {
      const found = stateOfPart(part);
      if (!found) {
        return;
      }
      found.state.inFlightBytes -= found.tracked.countedBytes;
      trackedParts.delete(part);
    },

    reportStoredBytes: (itemId, storedBytes): void => {
      const state = states.get(itemId);
      if (!state) {
        return;
      }
      state.storedBytes = storedBytes;
      emit(itemId, state);
    },
  };
};
